import { Injectable } from '@angular/core';
import { ShipmentLocationsService } from '@cemex-core/cmx-api';
import { ProductType } from '@cemex-core/cmx-api/models/business-lines/product-type';
import { PointOfDeliveryRequest } from '@cemex-core/cmx-api/models/point-of-delivery/point-of-delivery-req';
import { IShipmentLocation } from '@cemex-core/cmx-api/models/shipment-location/shipment-location';
import { IShipmentLocationRequest } from '@cemex-core/cmx-api/models/shipment-location/shipment-location-request';
import { IShipmentLocationResponse } from '@cemex-core/cmx-api/models/shipment-location/shipment-location-response';
import { FeatureToggleService } from '@cemex-core/cmx-rollout-toggle-v1';
import { CmxHttpClient } from '@cemex/cmx-client-layout';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Features } from '@ordersnproducts/features';
import * as fromOrdersFormsActions from '@ordersnproducts/modules/multiple-order-online/redux/actions/orders-forms.action';
import { updateForm } from '@ordersnproducts/modules/multiple-order-online/redux/actions/orders-forms.action';
import {
  getSelectedOrderForm,
  getSelectedOrderFormId,
} from '@ordersnproducts/modules/multiple-order-online/redux/selectors/orders-forms.selector';
import { getPurchaseOrderValidationData } from '@ordersnproducts/modules/multiple-order-online/redux/selectors/purchase-order.selector';
import { ModifyOrderUpdateData } from '@ordersnproducts/modules/new-modify-order/actions/order-detail.actions';
import { getJobsiteSelected } from '@ordersnproducts/modules/order-builder/modules/review/reducers/review.selectors';
import { getRmsSpecificationsIsModify } from '@ordersnproducts/modules/order-builder/modules/rms-specification/selectors';
import { SpecFieldNames } from '@ordersnproducts/modules/order-builder/modules/specification/constants/specification-field-names.enum';
import { CommercialChannel } from '@ordersnproducts/modules/shared/models/commercial-channel.enum';
import { AddressService } from '@ordersnproducts/modules/shared/services/address.service';
import { _safe } from '@ordersnproducts/modules/shared/utils/safe-get.method';
import { AnalyticsTrackingService } from 'app/services/analytics-tracking.service';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, delayWhen, filter, map, retryWhen, scan, switchMap, take, withLatestFrom } from 'rxjs/operators';
import { SHIPMENT_LOCATION_TYPE_ID } from '../../../../../../shared/constants/shipment-location-type-id';
import { IEffect } from '../../../../../../shared/modules/dynamic-effects/effect.interface';
import { getSelectedBusinessLine, OrderBuilderState } from '../../../../../reducers';
import { JOBSITE_SET, JobsiteSetAction } from '../../../actions/jobsite-actions/jobsite.actions';
import {
  LOAD,
  LOAD_COMPLETE,
  LoadPointOfDeliveryAddress,
  NEW_POINT_OF_DELIVERY_CREATE,
  NEW_POINT_OF_DELIVERY_CREATED,
  NewPointOfDeliveryCreateAction,
  NewPointOfDeliveryCreatedAction,
  NewPointOfDeliveryFailAction,
  PointOfDeliveryLoadAction,
  PointOfDeliveryLoadCompleteAction,
  PointOfDeliveryLoadFailAction,
  PointOfDeliveryRemoveSelectedAction,
  PointOfDeliverySetAction,
  ResetNewPointOfDeliveryAction,
} from '../../../actions/point-of-delivery-actions/point-of-delivery.actions';
import { PointOfDeliveryPayload } from '../../../components/point-of-delivery/new-pod-creation-modal/new-pod-creation-modal.component';

@Injectable()
export class PointOfDeliveryLoadEffects implements IEffect {
  constructor(
    private store: Store<OrderBuilderState>,
    private actions$: Actions,
    private shipmentLocationService: ShipmentLocationsService,
    private http: CmxHttpClient,
    private addressService: AddressService,
    private analyticsTrackingService: AnalyticsTrackingService,
    private featureToggle: FeatureToggleService,
  ) {}

  pointOfDeliveryRemoveSelected$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JOBSITE_SET),
      map((payload: JobsiteSetAction) => {
        return new PointOfDeliveryRemoveSelectedAction(null);
      }),
    ),
  );

  jobsiteSetLoad$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(JOBSITE_SET),
      map((action: JobsiteSetAction) => new PointOfDeliveryLoadAction(action.payload)),
    ),
  );

  pointOfDeliveryLoad$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD),
      switchMap((payload: PointOfDeliveryLoadAction) => {
        return this.store.select(getSelectedBusinessLine).pipe(
          take(1),
          map((selectedBusinessLine: ProductType) => _safe(() => selectedBusinessLine.productTypeId)),
          filter((businessLineId: number | null | undefined) => businessLineId != null),
          switchMap((businessLineId: number) => {
            return this.shipmentLocationService
              .getShipmentLocations(this.preparePointOfDeliveryLoadRequest(payload.payload, businessLineId))
              .pipe(
                map((shipmentLocationResponse: IShipmentLocationResponse) => {
                  return new PointOfDeliveryLoadCompleteAction(shipmentLocationResponse);
                }),
                catchError((error) => of(new PointOfDeliveryLoadFailAction(error))),
              );
          }),
        );
      }),
    ),
  );

  createNewPointOfDelivery$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType<NewPointOfDeliveryCreateAction>(NEW_POINT_OF_DELIVERY_CREATE),
      withLatestFrom(this.store.select(getJobsiteSelected)),
      switchMap(([action, jobsite]) => {
        return this.createPOD(action.payload).pipe(
          withLatestFrom(this.store.select(getSelectedBusinessLine), this.store.select(getPurchaseOrderValidationData)),
          switchMap(([payload, businessLine, data]) => {
            return this.shipmentLocationService
              .getShipmentLocations({
                shipmentLocationId: {
                  entityId: payload.jobsite.jobsiteId,
                  entityTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
                },
                shipmentLocationTypeId: [
                  SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY,
                  SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY_REQUEST,
                ].join(','),
                productLineId: businessLine?.productTypeId || data.productLine,
              })
              .pipe(
                switchMap((shipments) => {
                  const selectedShipment = shipments.shipmentLocations.find(
                    (s) => s.shipmentLocationId === payload.pointOfDeliveryId,
                  );

                  if (selectedShipment.shipmentLocationStatus.shipmentLocationStatusCode === 'ACTI') {
                    return of(payload);
                  } else {
                    return throwError(() => new Error('New Shipment Location is still not active'));
                  }
                }),
                retryWhen((errors) =>
                  errors.pipe(
                    scan((retryCount, error) => {
                      if (retryCount >= 4) {
                        throw error;
                      }
                      return retryCount + 1;
                    }, 0),
                    delayWhen(() => timer(5000)),
                  ),
                ),
              );
          }),
          map((pointOfDelivery) => new NewPointOfDeliveryCreatedAction(pointOfDelivery)),
          catchError((error) => {
            const errorMessage = _safe(() => error.error, error);
            const message_fail = errorMessage && errorMessage.httpMessage ? errorMessage.httpMessage : 'Error';

            this.analyticsTrackingService.PODCreationFails(
              CommercialChannel.CEMEX_GO,
              jobsite.shipmentLocationDesc,
              action.payload.pointOfDeliveryDesc,
              message_fail,
            );

            return of(new NewPointOfDeliveryFailAction(errorMessage));
          }),
        );
      }),
    ),
  );

  loadAddres$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(LOAD_COMPLETE),
      switchMap((action: any) => this.addressService.loadFormattedAddresses(action.payload['shipmentLocations'])),
    ),
  );

  loadNewAddress$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<NewPointOfDeliveryCreatedAction>(NEW_POINT_OF_DELIVERY_CREATED),
        withLatestFrom(
          this.store.select(getSelectedOrderForm),
          this.store.select(getSelectedOrderFormId),
          this.store.select(getRmsSpecificationsIsModify),
        ),
        map(([action, orderForm, orderFormId, isModify]) => {
          const newPointOfDelivery = action.payload;
          this.saveAnalyticsTracking(newPointOfDelivery, action);

          if (newPointOfDelivery) {
            const pointOfDeliveryId = newPointOfDelivery?.pointOfDeliveryId;
            this.store.dispatch(new ResetNewPointOfDeliveryAction());

            // This will update the form in New Multi Order
            if (orderForm) {
              this.store.dispatch(
                updateForm({
                  formId: orderForm.id,
                  formControlName: SpecFieldNames.POINT_OF_DELIVERY,
                  formControlvalue: pointOfDeliveryId,
                  formIsValid: orderForm.valid && true,
                }),
              );
            }
            // This will update the form in Modify Order
            if (this.featureToggle.isFeatureEnabled(Features.EnablePODCreateOnModifyOrderFeature) && isModify) {
              this.store.dispatch(
                new ModifyOrderUpdateData({
                  pointOfDelivery: {
                    pointOfDeliveryCode: newPointOfDelivery.pointOfDeliveryCode,
                    pointOfDeliveryDesc: newPointOfDelivery.pointOfDeliveryDesc,
                    pointOfDeliveryId: newPointOfDelivery.pointOfDeliveryId,
                    pointOfDeliveryType: {
                      pointOfDeliveryTypeCode: newPointOfDelivery.pointOfDeliveryType?.pointOfDeliveryTypeCode,
                    },
                  },
                }),
              );
            }
            const shipmentLocation = this.parsePointOfDeliveryToShipmentLocation(newPointOfDelivery);
            this.store.dispatch(new LoadPointOfDeliveryAddress(shipmentLocation));
            this.store.dispatch(fromOrdersFormsActions.unselectForm());
            this.store.dispatch(new PointOfDeliverySetAction(shipmentLocation));
          }
        }),
      ),
    {
      dispatch: false,
    },
  );

  public createPOD(payload: PointOfDeliveryPayload): Observable<PointOfDeliveryRequest> {
    const endpoint = `/v1/pjm/pointofdeliveries/`;
    return this.http.post<PointOfDeliveryRequest>(endpoint, payload);
  }

  /**
   * Converts a PointOfDeliveryRequest into the legacy IShipmentLocation
   */
  public parsePointOfDeliveryToShipmentLocation(pointOfDelivery: PointOfDeliveryRequest): IShipmentLocation {
    return {
      links: pointOfDelivery.links,
      shipmentLocationId: pointOfDelivery.pointOfDeliveryId,
      shipmentLocationCode: pointOfDelivery.pointOfDeliveryCode,
      shipmentLocationDesc: pointOfDelivery.pointOfDeliveryDesc,
      isMultipleOrder: null,
      isVariousLocation: null,
      shipmentLocationType: {
        shipmentLocationTypeCode: pointOfDelivery.pointOfDeliveryType?.pointOfDeliveryTypeCode,
        shipmentLocationTypeId: null,
        shipmentLocationTypeDesc: null,
        links: {
          self: '',
        },
      },
      shipmentLocationStatus: {
        shipmentLocationStatusCode: pointOfDelivery.status?.statusCode,
        shipmentLocationStatusDesc: pointOfDelivery.status?.statusDesc,
        shipmentLocationStatusId: pointOfDelivery.status?.statusId,
        links: {
          self: '',
        },
      },
      address: {
        addressId: pointOfDelivery.address?.addressId,
        links: pointOfDelivery.address?.links?.[0],
      },
      distance: {
        amount: null,
        unit: {
          unidId: null,
          unitCode: null,
        },
      },
      creationDate: null,
    };
  }

  private preparePointOfDeliveryLoadRequest(
    selectedJobsite: IShipmentLocation,
    businessLine: number,
  ): IShipmentLocationRequest {
    const payload: IShipmentLocationRequest = {
      shipmentLocationId: {
        entityId: selectedJobsite.shipmentLocationId,
        entityTypeId: SHIPMENT_LOCATION_TYPE_ID.JOBSITE,
      },
      shipmentLocationTypeId: [
        SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY,
        SHIPMENT_LOCATION_TYPE_ID.POINT_OF_DELIVERY_REQUEST,
      ].join(','),
      productLineId: businessLine,
    };

    return this.featureToggle.isFeatureEnabled(Features.ENABLE_VARIOUS_LOCATION_V4)
      ? { ...payload, shipmentLocationStatusId: undefined }
      : payload;
  }

  private saveAnalyticsTracking(newPointOfDelivery: PointOfDeliveryRequest, action: any) {
    this.analyticsTrackingService.PODCreationSuccess(
      CommercialChannel.CEMEX_GO,
      newPointOfDelivery.pointOfDeliveryDesc,
      newPointOfDelivery.address.addressId.toString(),
      action.type,
    );
  }
}
